import {
  agentFrameSchema,
  agentProposalSchema,
  type AgentFrame,
  type StoredAgentProposal,
  type AgentDashboardView,
} from '@forecastify/shared';
import { readSseBlocks, sseData } from '../../live/sse';
import type { SessionState } from '../../session';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C27.1 · one turn, read as it arrives. The client half of the stream.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **A plain async generator, not a hook.** What a screen needs is *frames in order*; wrapping
 * that in a hook would fix the state shape for every caller and make it untestable without a
 * renderer. `agent-route.tsx` owns the state; this owns the wire.
 */

/**
 * A refusal that arrived with a status code — so, before the stream opened.
 *
 * ⚠️⚠️ **CARRIES THE `code` AND NOT THE MESSAGE, PER RULE 5.** *"Clients branch on `code`, never on
 * `message`."* The server's `message` is English, may quote a third party, and is written for
 * whoever is on call; what the screen renders is the catalogue string for `code`, in HU or EN.
 *
 * ⚠️ **`agent_not_configured` arrives here too, and it is not an error state.** It is the deployment
 * saying it has declined the feature, and what the screen draws for it is C17's shell — the
 * greeting, the disabled composer, and a line saying the model is not connected. `agent-screen.tsx`
 * has the argument.
 */
export class AgentRefusal extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
  ) {
    super(`agent refused: ${code}`);
    this.name = 'AgentRefusal';
  }
}

/**
 * The `code` out of an error envelope, or a stand-in derived from the status.
 *
 * ⚠️ **A body that is not the envelope still produces a code**, because the alternative is a screen
 * that renders nothing for a 502 from a proxy that has never heard of this application. `502` is not
 * a code the catalogue knows, and `screen-state.tsx` already renders an unmapped code rather than
 * going blank — *"a screen that says nothing for an unmapped code is the original bug wearing
 * different clothes."*
 *
 * ⚠️ **Exported, and used by every agent request rather than reimplemented per call site.** The
 * first draft of `agent-route.tsx` had two more copies of this narrowing — one for the conversation
 * list, one for `POST /agent/conversations` — which is the duplicated predicate `hard-won.md` warns
 * about arriving as boilerplate: three readers of one envelope, and the one that drifts is the one
 * deciding whether a person sees *"not connected"* or nothing at all.
 */
export async function refusalFrom(response: Response): Promise<AgentRefusal> {
  const body: unknown = await response.json().catch(() => null);
  const code =
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    typeof body.error === 'object' &&
    body.error !== null &&
    'code' in body.error &&
    typeof body.error.code === 'string'
      ? body.error.code
      : `http_${String(response.status)}`;
  return new AgentRefusal(code, response.status);
}

/**
 * Send one message and yield the frames of the reply.
 *
 * ⚠️ **`session.request`, not a bare `fetch`.** It attaches the bearer token and the workspace
 * header, and does one refresh and one retry on a 401 — a turn that begins as an access token
 * expires would otherwise fail for a reason the person cannot act on. `live-provider.tsx` makes the
 * same note for the same helper.
 *
 * ⚠️⚠️ **A NON-OK RESPONSE IS READ AS AN ENVELOPE, AND THAT IS ONLY POSSIBLE BECAUSE THE SERVER
 * REFUSES BEFORE IT STREAMS.** `agent.controller.ts` awaits everything refusable before
 * `writeHead(200)`; so a 4xx or 5xx here has a JSON body and a real status, and anything that goes
 * wrong later arrives as an `error` **frame** with the same `{ code }` shape. The two halves of rule
 * 5 on one endpoint.
 */
/**
 * The frames of a stream, validated.
 *
 * ⚠️⚠️ **ONE READER FOR TWO ENDPOINTS, BECAUSE THEY ARE THE SAME STREAM.** `POST …/messages` and
 * `POST …/proposals/:id/accept` both answer with the agent's frame vocabulary — the second is *"the
 * write happened, now say what happened"* — so a second reader would be a second place for the
 * validation, the skip-on-malformed rule and the empty-body case to drift.
 */
async function* frames(response: Response): AsyncGenerator<AgentFrame, void, undefined> {
  if (!response.ok) throw await refusalFrom(response);
  /* ⚠️ A 200 with no body is not a stream. Treated as an upstream failure rather than as an empty
   * reply, because an empty reply would render as the model having said nothing. */
  if (response.body === null) throw new AgentRefusal('upstream_unavailable', response.status);

  for await (const block of readSseBlocks(response.body)) {
    /**
     * ⚠️ **Validated with the shared schema, never cast.** `hard-won.md` C19-1 is four chunks of
     * invisible links caused by trusting a shape nothing checked, and a frame has less to catch it
     * than a row does: an unrecognised `type` would simply be dropped by the reducer, and the
     * symptom is a bubble that never fills in.
     *
     * ⚠️ **An unparseable frame is SKIPPED, not thrown.** One malformed frame must not end a turn
     * that is otherwise arriving fine — and the server is the only thing writing them, so a frame
     * this fails on is a version skew between the two halves of `agentFrameSchema` rather than
     * anything a user did.
     */
    const parsed = agentFrameSchema.safeParse(sseData(block));
    if (parsed.success) yield parsed.data;
  }
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C27.3 · a confirmed write. The one request in this product that performs   │
 * │ a change and sends NO BODY.                                               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THERE IS NOTHING TO SEND, AND THAT IS THE POINT.** The server executes the input it stored
 * when the proposal was made — R-25: *"it executes the STORED input, never anything the client sends
 * back"*. A body here would make the endpoint a general-purpose write with the agent's schema and no
 * permission story of its own, reachable by anyone with a session. If a future version of this
 * function grows a `body`, that is the thing to stop.
 *
 * ⚠️ **It streams, because the model's account of what happened follows the write.** Same frames,
 * same reducer.
 */
export async function* agentAccept(
  request: SessionState['request'],
  proposalId: string,
  signal: AbortSignal,
): AsyncGenerator<AgentFrame, void, undefined> {
  const response = await request(`/agent/proposals/${proposalId}/accept`, {
    method: 'POST',
    headers: { accept: 'text/event-stream' },
    signal,
  });

  yield* frames(response);
}

/**
 * The person said no.
 *
 * ⚠️ **No stream and no model call**, so this is an ordinary request: `204`, or an envelope saying the
 * proposal was already answered. `agent.service.ts` argues why asking Anthropic to acknowledge a
 * dismissed card costs a turn's tokens for nothing.
 */
export async function agentDecline(
  request: SessionState['request'],
  proposalId: string,
): Promise<void> {
  const response = await request(`/agent/proposals/${proposalId}/decline`, { method: 'POST' });
  if (!response.ok) throw await refusalFrom(response);
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C46 · a card corrected. It re-proposes, and nothing is written.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE ONLY POST IN THIS FILE THAT SENDS VALUES, AND WHAT IT GETS BACK IS A PROPOSAL.** The
 * accept endpoint deliberately takes no body — R-25, and `agent.controller.ts` states it twice — so
 * this is not a shortcut past it: the server merges these values into the input it stored, runs the
 * write tool's own `run` over the result, and answers with a **new card somebody still has to
 * press**. A client cannot write through this.
 *
 * ⚠️ **Parsed, not asserted.** The reply is the same shape a `proposal` frame carries, so it goes
 * through the same schema — a server that changed it would fail here rather than render a card with
 * an undefined field on it.
 */
export async function agentRevise(
  request: SessionState['request'],
  proposalId: string,
  values: Readonly<Record<string, string | null>>,
): Promise<StoredAgentProposal> {
  const response = await request(`/agent/proposals/${proposalId}/revise`, {
    method: 'POST',
    body: JSON.stringify({ values }),
  });
  if (!response.ok) throw await refusalFrom(response);

  const parsed = agentProposalSchema.safeParse(await response.json());
  /* ⚠️ 502 rather than a made-up code: what failed is upstream of this screen, and the catalogue
   * has one sentence for exactly that. */
  if (!parsed.success) throw new AgentRefusal('upstream_unavailable', 502);

  /* ⚠️ **`pending`, supplied here rather than sent.** The wire shape of a fresh offer carries no
   * state — a proposal frame does not either — and the card it replaces was `working` while the
   * request was in flight. */
  return { ...parsed.data, state: 'pending' };
}

export async function* agentTurn(
  request: SessionState['request'],
  conversationId: string,
  text: string,
  signal: AbortSignal,
  /* 19 Oct 2026 · a dashboard thread's view — what the asker is looking at (AG-1). */
  view?: AgentDashboardView,
): AsyncGenerator<AgentFrame, void, undefined> {
  const response = await request(`/agent/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: { accept: 'text/event-stream' },
    body: JSON.stringify(view === undefined ? { text } : { text, view }),
    signal,
  });

  yield* frames(response);
}
